/**
 * i18n Module Entry Point
 *
 * Exports all i18n types, hooks, and initializes i18next.
 * Supports automatic language detection from localStorage, navigator, and HTML tag.
 *
 * @module i18n
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './en.json';
import zhTW from './zh-TW.json';

// Initialize i18next with language detection
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      'zh-TW': { translation: zhTW },
    },
    fallbackLng: 'en',
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already escapes values
    },
  });

// Re-export types and hooks
export type { TranslationKey, IUITranslations } from './types';
export { useTypedTranslation } from './useTypedTranslation';
export type { TranslationOptions, TypedTranslationResult } from './useTypedTranslation';
export { useLanguageSwitch, SUPPORTED_LANGUAGES } from './useLanguageSwitch';
export type { LanguageConfig, UseLanguageSwitchResult } from './useLanguageSwitch';
export { LanguageSwitcher, languageSwitcherVariants } from './LanguageSwitcher';
export type { LanguageSwitcherProps, LanguageSwitcherVariants } from './LanguageSwitcher';
export {
  getPluralForm,
  getPluralRules,
  getPluralCategories,
  hasPluralForms,
  PLURAL_RULES,
} from './pluralization';
export type { PluralCategory, PluralRuleSet } from './pluralization';

// Date/Time formatters (SPEC-017 Task 6)
export {
  formatDate,
  formatTime,
  formatDateTime,
  formatRelativeTime,
  formatShortDate,
  formatSmartDate,
  SUPPORTED_DATE_LOCALES,
} from './formatters';
export type { SupportedDateLocale } from './formatters';

// Error localization (SPEC-017 Task 12)
export {
  ERROR_CODE_I18N_MAP,
  isApiError,
  isNetworkError,
  isOffline,
  getErrorI18nKey,
  getLocalizedErrorMessage,
  createLocalizedError,
  errorLocalization,
} from './error-localization';
export type {
  LocalizedApiError,
  TranslationFunction,
  LocalizedErrorOptions,
} from './error-localization';

// RTL Support (SPEC-017 Task 13)
export {
  RTL_LANGUAGES,
  isRTL,
  getTextDirection,
  getRTLClass,
  getLanguageClass,
  updateDocumentDirection,
} from './rtl-support';
export type { TextDirection } from './rtl-support';

// Lazy Loading (SPEC-017 Task 16)
export {
  loadTranslation,
  preloadTranslation,
  isTranslationLoaded,
  clearTranslationCache,
  switchLanguageWithMetrics,
  measureLanguageSwitchTime,
  getLoadingMetrics,
  clearLoadingMetrics,
  TranslationCache,
  PERFORMANCE_THRESHOLDS,
} from './lazy-loader';
export type { LoadTranslationResult, SwitchLanguageResult, LoadingMetrics } from './lazy-loader';

export { i18n };
